/**
 * Primitivos da paleta — Camada 1 do design system.
 *
 * Único arquivo de código onde cores literais são permitidas (ver `overrides` do ESLint).
 * Componentes NUNCA consomem estes valores diretamente: eles alimentam os tokens semânticos
 * de `global.ts`, que é a interface pública do sistema.
 *
 * Paleta "Financial Infrastructure" — Brand Book Cap. 05.
 * Contrastes verificados por `npm run verify:contrast`.
 */

/** Cores institucionais do Brand Book. */
export const palette = {
	/** Deep Graphite — primária. 13.97:1 sobre offWhite. */
	graphite: '#202930',
	/** Intelligent Mint — secundária. Só como superfície ou sobre fundo escuro. */
	mint: '#7EE1AC',
	/** Digital Teal — destaque. Só como superfície ou sobre fundo escuro. */
	teal: '#29C7B8',
	/** Slate Blue — suporte 1. 6.42:1 sobre offWhite. */
	slate: '#445E73',
	/** Soft Stone — suporte 2. Superfície recuada. */
	stone: '#DCE3E0',
	/** Off White — neutra. Fundo da aplicação. */
	offWhite: '#F7F9F8',
	/** Branco puro — superfícies elevadas (cards, modais). */
	white: '#FFFFFF',
	/** Graphite escurecido — estado hover/active sobre superfícies primárias. */
	graphiteDark: '#0F1519',
} as const;

/**
 * Derivados acessíveis.
 *
 * `teal` e `mint` reprovam WCAG AA como texto sobre fundo claro (1.99:1 e 1.50:1).
 * Estes tons foram escurecidos em HLS, preservando o matiz, até atingir o alvo —
 * são o que deve ser usado quando a cor precisa carregar texto ou borda sobre claro.
 */
export const derived = {
	/** 4.57:1 sobre offWhite — texto e links de ação. */
	tealDeep: '#1A7F76',
	/** 3.01:1 sobre offWhite — bordas e ícones (AA Large). */
	tealMid: '#21A195',
	/** 4.50:1 sobre offWhite — texto de sucesso. */
	mintDeep: '#1E834D',
	/** 3.09:1 sobre offWhite — bordas e ícones de sucesso (AA Large). */
	mintMid: '#26A260',
	/** 2.31:1 sobre offWhite — texto desabilitado (isento de AA por WCAG 1.4.3). */
	slateLight: '#8FA9BF',
} as const;

/**
 * Semânticas.
 *
 * O Brand Book não define vermelho nem âmbar, mas erro e alerta são inevitáveis numa
 * aplicação de crédito. Estes tons são dessaturados para conviver com o território
 * "quiet authority", e passam AA tanto como texto sobre fundo claro quanto como fundo
 * de texto branco.
 */
export const semantic = {
	success: '#1E834D',
	error: '#B3261E',
	warning: '#8A5A00',
	info: '#445E73',
} as const;

/**
 * Superfícies semânticas suaves — fundos de alerta, banner e chip.
 *
 * Cada tom foi clareado até que a semântica correspondente atinja 4.5:1 como texto
 * sobre ele, de modo que `--error-text` sobre `--error-surface` seja sempre legível.
 * `--text-primary` também passa sobre todos (≥12.5:1).
 */
export const semanticSurface = {
	success: '#F2FBF6', // success-text: 4.51:1
	error: '#F7E9E8', // error-text:   5.53:1
	warning: '#F7F2E8', // warning-text: 5.31:1
	info: '#ECF0F4', // info-text:    5.93:1
} as const;

/**
 * Visualização de dados — sequência categórica ordenada.
 *
 * A ordem alterna luminâncias de propósito: séries vizinhas ficam separadas por brilho,
 * não apenas por matiz, o que preserva a distinção em escala de cinza e para daltônicos.
 * Não reordenar sem revalidar o contraste entre posições adjacentes.
 */
export const dataviz = [
	'#1A7F76',
	'#7EE1AC',
	'#445E73',
	'#29C7B8',
	'#202930',
	'#1E834D',
	'#8FA9BF',
	'#0F4A52',
] as const;

/**
 * Fundos de avatar.
 *
 * Subconjunto da paleta escolhido por um critério objetivo: são as únicas cores
 * institucionais que atingem 4.5:1 com a inicial em branco por cima. Os tons claros
 * (mint 1.58, teal 2.11, slate light 2.44) ficam de fora — tornariam a inicial ilegível.
 *
 * A escolha por usuário é determinística (hash do nome), então a mesma pessoa mantém
 * sempre a mesma cor.
 */
export const avatarSurface = [
	'#1A7F76', // 4.84:1 com branco
	'#445E73', // 6.79:1
	'#202930', // 14.78:1
	'#1E834D', // 4.76:1
	'#0F4A52', // 9.90:1
] as const;
